import React, { createContext, useContext, useLayoutEffect, useState, useSyncExternalStore } from "react"

// Each row subscribes to its own answer, so checking a box re-renders that row only.
const createTreeViewStore = () => {
  let snapshot  = { selected: new Set(), search: null, onToggle: () => {} }
  const listeners = new Set()
  return {
    get:       () => snapshot,
    set:       next => { snapshot = next; listeners.forEach(listener => listener()) },
    subscribe: listener => { listeners.add(listener); return () => listeners.delete(listener) },
  }
}

const TreeViewContext = createContext(null)

export const TreeViewProvider = ({ selected, search, onToggle, scrollRef, children }) => {
  const [store] = useState(createTreeViewStore)
  store.scrollRef = scrollRef

  useLayoutEffect(() => {
    store.set({ selected, search, onToggle })
  }, [store, selected, search, onToggle])

  return <TreeViewContext.Provider value={store}>{children}</TreeViewContext.Provider>
}

const useTreeView = select => {
  const store = useContext(TreeViewContext)
  return useSyncExternalStore(store.subscribe, () => select(store.get()))
}

export const searchClassOf = (search, uid) => {
  if (!search) return ""
  if (search.currentUid === uid)  return "qb-hit qb-hit--current"
  if (search.matches.has(uid))    return "qb-hit"
  if (search.relevant.has(uid))   return ""
  return "qb-faded"
}

export const useSearchClass = uid => useTreeView(view => searchClassOf(view.search, uid))

export const useScrollRoot = () => useContext(TreeViewContext).scrollRef

export const useSearching = () => useTreeView(view => !!view.search)

export const useRowSelection = uid => {
  const store   = useContext(TreeViewContext)
  const checked = useTreeView(view => view.selected.has(uid))
  return {
    checked,
    onToggle: withShift => store.get().onToggle(uid, withShift),
  }
}
